import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import styles from './LoginForm.module.css';
import TermsBottomSheet from '../Legal/TermsBottomSheet';

interface LoginFormProps {
  onClose: () => void;
  mode: 'login' | 'register';
}

const LoginForm: React.FC<LoginFormProps> = ({ onClose, mode }) => {
  const { login, register } = useAuth();
  const [currentMode, setCurrentMode] = useState(mode);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [showTerms, setShowTerms] = useState(false);

  // Disable body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    
    // Re-enable scroll when component unmounts
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  const validatePassword = (password: string): string | null => {
    if (password.length < 8) {
      return 'Password must be at least 8 characters long';
    }
    return null;
  };

  const validatePasswordMatch = (password: string, confirmPassword: string): string | null => {
    if (password !== confirmPassword) {
      return 'Passwords do not match';
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      // Use currentMode instead of mode to match what the user sees
      if (currentMode === 'login') {
        await login(email, password);
      } else {
        // Validate password for registration
        const passwordError = validatePassword(password);
        if (passwordError) {
          setError(passwordError);
          setIsLoading(false);
          return;
        }

        const matchError = validatePasswordMatch(password, confirmPassword);
        if (matchError) {
          setError(matchError);
          setIsLoading(false);
          return;
        }

        await register(email, password, firstName, lastName);
      }
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'An error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const toggleMode = () => {
    setCurrentMode(currentMode === 'login' ? 'register' : 'login');
    setError('');
    // Clear form data when switching modes
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setFirstName('');
    setLastName('');
  };

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <button className={styles.closeButton} onClick={onClose}>
          ×
        </button>
        
        <h2 className={styles.title}>
          {currentMode === 'login' ? 'Sign In' : 'Create Account'}
        </h2>

        <form onSubmit={handleSubmit} className={styles.form}>
          {currentMode === 'register' && (
            <>
              <div className={styles.inputGroup}>
                <input
                  type="text"
                  placeholder="First Name (optional)"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className={styles.input}
                />
              </div>
              <div className={styles.inputGroup}>
                <input
                  type="text"
                  placeholder="Last Name (optional)"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className={styles.input}
                />
              </div>
            </>
          )}
          
          <div className={styles.inputGroup}>
            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className={styles.input}
            />
          </div>
          
          <div className={styles.inputGroup}>
            <input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className={styles.input}
            />
            {currentMode === 'register' && (
              <div className={styles.passwordRequirements}>
                Password must be at least 8 characters long
              </div>
            )}
          </div>

          {currentMode === 'register' && (
            <div className={styles.inputGroup}>
              <input
                type="password"
                placeholder="Confirm Password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                className={styles.input}
              />
            </div>
          )}

          {currentMode === 'register' && (
            <div className={styles.inputGroup}>
              <div className={styles.termsRow}>
                <input
                  id="modal-accept-terms"
                  type="checkbox"
                  checked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e.target.checked)}
                  disabled={isLoading}
                  style={{ marginTop: 2 }}
                />
                <label htmlFor="modal-accept-terms" style={{ color: 'var(--color-text-secondary)', fontSize: 13 }}>
                  I have read and agree to the{' '}
                  <a
                    href="#"
                    onClick={(e) => { e.preventDefault(); setShowTerms(true); }}
                    className={styles.forgotPasswordLink}
                    role="button"
                    aria-haspopup="dialog"
                  >
                    Terms & Conditions
                  </a>.
                </label>
              </div>
            </div>
          )}

          {error && <div className={styles.error}>{error}</div>}

          <button 
            type="submit" 
            className={styles.submitButton}
            disabled={isLoading || (currentMode === 'register' && !acceptedTerms)}
          >
            {isLoading ? 'Loading...' : currentMode === 'login' ? 'Sign In' : 'Create Account'}
          </button>

          {currentMode === 'login' && (
            <div className={styles.forgotPassword}>
              <Link to="/password-reset" className={styles.forgotPasswordLink}>
                Forgot your password?
              </Link>
            </div>
          )}
        </form>

        <div className={styles.footer}>
          <button 
            type="button" 
            onClick={toggleMode}
            className={styles.toggleButton}
          >
            {currentMode === 'login' 
              ? "Don't have an account? Sign up" 
              : "Already have an account? Sign in"
            }
          </button>
        </div>
      </div>
      <TermsBottomSheet open={showTerms} onClose={() => setShowTerms(false)} />
    </div>
  );
};

export default LoginForm;
